import { Form, Field } from "web/components"
import { BooleanSelect } from "web/components/Form/Controls"

import {
  useAccountValues,
  useFieldVisibility,
  useFieldLookupValues,
} from "web/hooks/data"
import { Suspense } from "react"
import { promoteKey } from "web/services/helpers"
import { formValuesAreDifferent } from "web/components/Form/helpers"

function EditableCell({ subjectType, topic, rowData, field }) {
  const hasTopic = !!topic

  const [accountValues, localUpdateSubjectAccountValues] = useAccountValues({
    subjectType,
    subjectId: rowData.id,
    isTopicRecord: hasTopic,
  })

  const fieldVisibility = useFieldVisibility({
    subjectType,
    subjectId: rowData.id,
    fieldId: field.id,
    isTopicRecord: hasTopic,
  })

  const validLookupValues = useFieldLookupValues(field, subjectType, rowData.id)

  async function updateFieldValue(value) {
    if (!formValuesAreDifferent(accountValues[field.key], value)) return

    const update = field.is_editable_attribute
      ? {
          [field.name]: value?.value ?? null,
          [`${field.name}_unit`]: value?.unit,
        }
      : { [field.key]: value }

    if (field.prioritize_default_expression) {
      return await rowData.update(update)
    }

    const formattedUpdate = {
      [field.key]: { value: value, form_value: value },
    }

    const newLocalData = {
      ...rowData,
      values: { ...rowData.values, ...formattedUpdate },
    }

    localUpdateSubjectAccountValues(field.key, value)
    return await rowData.update(update, newLocalData)
  }

  const globalFormValue = promoteKey(rowData.values, "value")

  if (field.is_editable_attribute && !(field.key in accountValues)) {
    accountValues[field.key] = globalFormValue[field.key]
  }

  const fieldWithVisibility = {
    ...field,
    isVisible: fieldVisibility.is_visible,
  }

  const hasLookupValues = field.lookup_values?.length > 0

  function lookupValueProps() {
    if (field.kind === "Boolean") {
      return { Control: BooleanSelect }
    }

    if (!hasLookupValues) return {}

    return {
      options: validLookupValues,
      optionsLoading: validLookupValues.isLoading,
    }
  }

  return (
    <Form
      value={accountValues}
      globalValue={globalFormValue}
      fields={[fieldWithVisibility]}
      onFieldChanged={(_, value) => updateFieldValue(value)}
      render={() => (
        <Field
          fieldKey={field.key}
          autoFocus={true}
          hideLabel={true}
          hideError={true}
          {...lookupValueProps()}
        />
      )}
    />
  )
}

export default function SuspensefulEditableCell(props) {
  return (
    <Suspense fallback={null}>
      <EditableCell {...props} />
    </Suspense>
  )
}
